import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiErrorText, apiRequest } from '../../utils';
import './MemberHome.css';
import './CompanyEvents.css';
import './CompanyTournament.css';

/* The houses on this day: who competes for which, and where their points came from.

   ★★ A house has no points of its own. Its total is the plain sum of the totals of the teams
   competing for it — the same number the back office has always shown — so awarding points to a
   PERSON puts them on that person's team, and they reach the house through it exactly once. What
   this screen adds is the NAME next to them. The three lines under each house (people, the house's
   own awards, the rest) are slices of the total, never additions to it, and they are shown apart
   for the reason the ranking screen shows battle and bonus apart: a single number with no
   breakdown is a standing nobody can check, and the first question after a prize-giving is always
   "where did those points come from".

   ★★ The house a point lands on is the TEAM's house, not the member's. Membership is standing —
   written where the company's values are — while which house a team competes for is set per event,
   and the shuffle packs teams by COMPANY. So the two diverge normally, not exceptionally. Every
   person in the picker carries that state, and somebody whose team plays for another house says so
   on their own row BEFORE the click, not in a toast afterwards.

   ★ Somebody with no team today cannot be given points at all: there is nothing for them to land
   on, and the ledger column that would hold it is NOT NULL. They are listed, greyed, with the way
   to fix it — a refusal that names the next step rather than a name that silently does nothing.

   ★ Houses are INVENTED elsewhere — on "What we stand for", beside the values and the themes,
   because a house outlives the day. This screen picks from that shelf and links across when it is
   empty. */

const CompanyEventHouses = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [open, setOpen] = useState(null);        // which house is expanded
  const [award, setAward] = useState(null);      // {house_id, person_id?, points, comment}

  const load = useCallback(() => {
    apiRequest('GET', `/company/event/${eventId}/houses`)
      .then(setData)
      .catch((e) => setNotice(apiErrorText(t, e)));
  }, [eventId, t]);

  useEffect(() => { load(); }, [load]);

  const call = (method, path, body) => {
    setBusy(true);
    setNotice(null);
    return apiRequest(method, `/company/event/${eventId}${path}`, body)
      .then((res) => { setData((d) => ({ ...d, ...res })); return res; })
      .catch((e) => { setNotice(apiErrorText(t, e)); throw e; })
      .finally(() => setBusy(false));
  };

  if (!data) {
    return (
      <Base activeNav="home">
        <Card><p className="ct-field__hint">{notice || t('pleaseWait')}</p></Card>
      </Base>
    );
  }

  const houses = data.houses || [];
  const library = data.library || [];
  const teams = data.teams || [];
  const canManage = !!data.can_manage;
  const locked = !canManage || busy;

  const submitAward = () => {
    if (!award || !award.points) return;
    const path = award.person_id
      ? '/houses/points'
      : `/houses/${award.house_id}/points`;
    call('POST', path, {
      person_id: award.person_id,
      house_id: award.house_id,
      points: award.points,
      comment: award.comment,
    }).then(() => setAward(null)).catch(() => {});
  };

  return (
    <Base activeNav="home">
      <Card>
        <Back to={`/company-event/${eventId}/tournament`} />
        <Title my="1" className="mt-3">
          <Icon name="flag" theme="primary" className="me-2" />
          {t('housesTitle')}
        </Title>

        {notice && <p className="ce-note ce-note--warn">{notice}</p>}

        {/* The shelf is empty: there is nothing to do here yet, and the fix is on another
            screen. Say which one rather than showing an empty list. */}
        {houses.length === 0 && library.length === 0 && (
          <>
            <p className="ce-note">{t('housesNoneOwned')}</p>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate('/houses')}
            >
              <Icon name="plus" /> {t('housesGoMake')}
            </button>
          </>
        )}

        {/* THE STANDING, first, because it is what anybody opens this screen for on the day. */}
        {houses.length > 0 && (
          <>
            <p className="ct-pick__label">{t('housesStanding')}</p>
            <ol className="rk-list">
              {houses.map((h, index) => (
                <li key={h.house_id} className="rk-row">
                  <span className="rk-row__pos">{index + 1}</span>
                  <span
                    className="bravos-themerow__img"
                    style={{ background: h.color || '#5a6472', width: 14, height: 14 }}
                  />
                  <span className="rk-row__name">{h.name}</span>
                  <span className="rk-row__split">
                    <span title={t('rankingBattlePoints')}>{h.battle}</span>
                    {!!h.bonus && (
                      <span className="rk-row__bonus" title={t('rankingBonusPoints')}>
                        {`+${h.bonus}`}
                      </span>
                    )}
                  </span>
                  <span className="rk-row__total">{h.total}</span>
                </li>
              ))}
            </ol>
            <p className="ct-field__hint">{t('housesStandingLegend')}</p>
            {/* ★★ Said here, on the screen that shows it: this total is the plain SUM of the
                totals of a house's teams, so a house that fields four teams leads one that fields
                two before anybody has played. On a Hercules day every house enters the same
                number and that is fine; on a company day it is not, which is why the cup scores
                PLACE instead and is the standing that counts. */}
            <p className="ct-field__hint">{t('housesRawWarning')}</p>
            <button
              type="button"
              className="bravos-submit bravos-submit--ghost"
              onClick={() => navigate(`/company-event/${eventId}/house-cup`)}
            >
              <Icon name="trophy" /> {t('housesToCup')}
            </button>
          </>
        )}

        {/* EACH HOUSE: where its total came from, and who is in it. */}
        {houses.map((h) => (
          <div key={h.house_id}>
            <div className="bravos-themerow">
              <span
                className="bravos-themerow__img"
                style={{ background: h.color || '#5a6472' }}
              />
              <span className="bravos-themerow__body">
                <span className="bravos-themerow__name">{h.name}</span>
                {/* The three slices, spelled out. They sum to the total by construction —
                    "unattributed" is the battle points plus everything awarded without a name. */}
                <span className="bravos-themerow__when">
                  {t('housesSplit', {
                    people: h.by_person_total,
                    house: h.house_direct,
                    rest: h.unattributed,
                  })}
                </span>
              </span>
              <button
                type="button"
                className="bravos-member__makeadmin"
                onClick={() => setOpen(open === h.house_id ? null : h.house_id)}
              >
                {open === h.house_id ? t('close') : t('housesDetail')}
              </button>
              <button
                type="button"
                className="bravos-member__makeadmin"
                onClick={() => setAward({ house_id: h.house_id, points: '', comment: '' })}
              >
                {t('housesAward')}
              </button>
              {canManage && (
                <button
                  type="button"
                  className="bravos-member__remove"
                  onClick={() => call('DELETE', `/houses/${h.id}`).catch(() => {})}
                  disabled={locked}
                  title={t('housesTakeOff')}
                >
                  <Icon name="trash" />
                </button>
              )}
            </div>

            {open === h.house_id && (
              <div className="bravos-form">
                <p className="ct-pick__label">{t('housesTeams')}</p>
                {h.teams.length === 0 && <p className="ce-note">{t('housesNoTeams')}</p>}
                {h.teams.map((tm) => (
                  <div key={tm.team_id} className="rk-row">
                    <span className="rk-row__name">{tm.name}</span>
                    <span className="rk-row__total">{tm.total}</span>
                  </div>
                ))}

                <p className="ct-pick__label mt-2">{t('housesPeople')}</p>
                {(h.members || []).length === 0 && (
                  <p className="ce-note">{t('housesNoMembers')}</p>
                )}
                {(h.members || []).map((m) => {
                  const earned = (h.by_person || [])
                    .find((p) => p.person_id === m.person_id);
                  return (
                    <div key={m.member_id} className="bravos-themerow">
                      <span className="bravos-themerow__body">
                        <span className="bravos-themerow__name">{m.name}</span>
                        {/* Where this person's points would go, said on their own row. */}
                        <span className="bravos-themerow__when">
                          {m.state === 'not_playing' && t('housesNotPlaying')}
                          {m.state === 'other_house'
                            && t('housesOtherHouse', {
                              team: m.team_name,
                              house: m.team_house_name || '—',
                            })}
                          {m.state === 'with_us' && m.team_name}
                        </span>
                      </span>
                      {!!earned && <span className="rk-row__total">{earned.points}</span>}
                      <button
                        type="button"
                        className="bravos-member__makeadmin"
                        disabled={busy || m.state === 'not_playing'}
                        onClick={() => setAward({
                          house_id: h.house_id,
                          person_id: m.person_id,
                          name: m.name,
                          warn: m.state === 'other_house' ? m.team_house_name : null,
                          points: '',
                          comment: '',
                        })}
                      >
                        {t('housesAward')}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}

        {/* AWARDING. One form for both, because they are the same act with a different subject:
            a person, or the house itself. The house arm prints its arithmetic first — 50 points
            to a house of three teams is 150, and that has always been what it means. */}
        {award && (
          <div className="bravos-form">
            <p className="ct-pick__label">
              {award.person_id
                ? t('housesAwardPerson', { name: award.name })
                : t('housesAwardHouse')}
            </p>
            {award.warn && (
              <p className="ce-note ce-note--warn">
                {t('housesAwardWarnOtherHouse', { house: award.warn })}
              </p>
            )}
            {!award.person_id && (
              <p className="ce-note">
                {t('housesAwardFanout', {
                  teams: (houses.find((h) => h.house_id === award.house_id)
                    || { teams: [] }).teams.length,
                  total: (Number(award.points) || 0)
                    * (houses.find((h) => h.house_id === award.house_id)
                      || { teams: [] }).teams.length,
                })}
              </p>
            )}
            <input
              type="number"
              value={award.points}
              placeholder={t('housesAwardPoints')}
              onChange={(e) => setAward({ ...award, points: e.target.value })}
            />
            <input
              type="text"
              value={award.comment}
              maxLength={150}
              placeholder={t('housesAwardComment')}
              onChange={(e) => setAward({ ...award, comment: e.target.value })}
            />
            <div className="bravos-form__row">
              <button
                type="button"
                className="bravos-submit"
                onClick={submitAward}
                disabled={busy || !award.points}
              >
                {busy ? t('pleaseWait') : t('save')}
              </button>
              <button
                type="button"
                className="bravos-submit bravos-submit--ghost"
                onClick={() => setAward(null)}
              >
                {t('cancel')}
              </button>
            </div>
          </div>
        )}

        {/* SETTING UP THE DAY — the admin's half. */}
        {canManage && (
          <>
            {data.unassigned > 0 && (
              <p className="ce-note ce-note--warn">
                {t('housesUnassigned', { count: data.unassigned })}
              </p>
            )}

            {library.length > 0 && (
              <>
                <p className="ct-pick__label mt-2">{t('housesAddToDay')}</p>
                <div className="ct-chips">
                  {library.map((h) => (
                    <button
                      key={h.id}
                      type="button"
                      className="ct-chip ct-chip--pick"
                      disabled={locked}
                      onClick={() => call('POST', '/houses', { house_id: h.id }).catch(() => {})}
                    >
                      <span
                        className="bravos-themerow__img"
                        style={{ background: h.color || '#5a6472', width: 12, height: 12 }}
                      />
                      {h.name}
                    </button>
                  ))}
                </div>
              </>
            )}

            {houses.length > 0 && teams.length > 0 && (
              <>
                <p className="ct-pick__label mt-2">{t('housesSortTeams')}</p>
                {teams.map((tm) => (
                  <div key={tm.team_id} className="bravos-themerow">
                    <span className="bravos-themerow__body">
                      <span className="bravos-themerow__name">{tm.name}</span>
                    </span>
                    <select
                      value={tm.house_id || ''}
                      disabled={locked}
                      onChange={(e) => call('PUT', '/houses/teams', {
                        assignments: { [tm.team_id]: e.target.value || null },
                      }).catch(() => {})}
                    >
                      <option value="">{t('housesNoHouse')}</option>
                      {houses.map((h) => (
                        <option key={h.house_id} value={h.house_id}>{h.name}</option>
                      ))}
                    </select>
                  </div>
                ))}
                <button
                  type="button"
                  className="bravos-submit bravos-submit--ghost"
                  disabled={locked}
                  onClick={() => call('POST', '/houses/shuffle').catch(() => {})}
                >
                  <Icon name="shuffle" /> {t('housesShuffle')}
                </button>
                <p className="ct-field__hint">{t('housesShuffleNote')}</p>
              </>
            )}
          </>
        )}

        {/* THE LEDGER — what was given, by whom, and the way back. */}
        {(data.ledger || []).length > 0 && (
          <>
            <p className="ct-pick__label mt-2">{t('housesLedger')}</p>
            {(data.ledger || []).map((row) => (
              <div key={row.id} className="bravos-themerow">
                <span className="bravos-themerow__body">
                  <span className="bravos-themerow__name">
                    {row.person_name || row.team_name} · {row.points}
                  </span>
                  <span className="bravos-themerow__when">
                    {[row.comment, row.date].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <button
                  type="button"
                  className="bravos-member__remove"
                  disabled={busy}
                  title={t('housesRevoke')}
                  onClick={() => call('DELETE', `/houses/points/${row.id}`).catch(() => {})}
                >
                  <Icon name="trash" />
                </button>
              </div>
            ))}
          </>
        )}

        <div className="ce-actions">
          <button
            type="button"
            className="bravos-submit bravos-submit--ghost"
            onClick={() => navigate(`/company-event/${eventId}/ranking`)}
          >
            <Icon name="leaderboard" /> {t('tournamentRanking')}
          </button>
        </div>
      </Card>
    </Base>
  );
};

export default CompanyEventHouses;
